// Communication — 4 KPI cards (Appels / Emails / SMS / WhatsApp).
import { useEffect, useState } from "react";
import { Phone, Mail, MessageCircle, MessageSquare, Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Kpi({ icon: Icon, label, value, color, hint }) {
  return (
    <div className="rounded-xl p-3 flex items-center gap-3"
      style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `color-mix(in srgb, ${color} 14%, transparent)`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{label}</p>
        <p className="text-xl font-bold" style={{ color: "var(--oh-text)" }}>{value}</p>
        {hint && <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{hint}</p>}
      </div>
    </div>
  );
}

export default function CommunicationStatsWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // PN-14 : « Appels » est compté PAR LE SERVEUR sur tous les appels (avant : les 100 derniers reçus par l'écran, plafonnés sans le dire).
        const calls = await axios.get(`${API}/api/calls/stats/overview`, { headers: auth() });
        let sms = { sent: 0 }, email = { sent: 0 };
        try {
          const [s, e] = await Promise.all([
            axios.get(`${API}/api/communication/stats?channel=sms`, { headers: auth() }),
            axios.get(`${API}/api/communication/stats?channel=email`, { headers: auth() }),
          ]);
          sms = s.data; email = e.data;
        } catch {}
        // WhatsApp : messages que l'assistant a vus (journal d'activité), compté par le serveur ; « — » seulement si la lecture échoue.
        let whatsapp = null;
        try {
          const w = await axios.get(`${API}/api/bailey/activity/stats`, { headers: auth() });
          whatsapp = w.data?.messages_seen ?? null;
        } catch {}
        if (!cancelled) setStats({ calls: calls.data?.total ?? 0, sms: sms.sent || 0, email: email.sent || 0, whatsapp });
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="rounded-xl p-5 flex items-center justify-center"
    style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
    <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="communication-stats-widget">
      <Kpi icon={Phone} label="Appels" value={stats.calls} color="var(--oh-stat-blue-text)" hint="tous les appels" />
      <Kpi icon={Mail} label="Emails envoyés" value={stats.email} color="var(--oh-stat-purple-text)" hint="par la plateforme, hors réponses du Bureau" />
      <Kpi icon={MessageCircle} label="SMS envoyés" value={stats.sms} color="var(--oh-stat-amber-text)" />
      <Kpi icon={MessageSquare} label="Messages WhatsApp vus" value={stats.whatsapp ?? "—"} color="var(--oh-stat-green-text)" />
    </div>
  );
}
